import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { getSoldRows, getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice, getTradeLock } from './helpers';
import { findSkinImage } from './skinIndex';

// Overview 5 — "OVERWATCH" tactical terminal.
// Match-HUD-meets-Bloomberg: graphite, amber chrome, phosphor green P/L,
// and the CS2 rarity ramp as the categorical palette (assigned by value
// rank, so the biggest category reads as gold tier).
const MONTH_LABELS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

// CS2 rarity ramp, most valuable tier first.
const RARITY = [
  { color: '#e4ae39', tier: 'GOLD' },
  { color: '#eb4b4b', tier: 'COVERT' },
  { color: '#d32ce6', tier: 'CLASSIFIED' },
  { color: '#8847ff', tier: 'RESTRICTED' },
  { color: '#4b69ff', tier: 'MIL-SPEC' },
  { color: '#5e98d9', tier: 'INDUSTRIAL' },
  { color: '#b0c3d9', tier: 'CONSUMER' },
];

const COND_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field Tested': 'FT',
  'Well Worn': 'WW', 'Battle Scarred': 'BS',
};

const fmt = (n) => {
  const abs = Math.abs(n);
  return abs >= 1000 ? abs.toLocaleString('en-US', { maximumFractionDigits: 0 }) : abs.toFixed(2);
};
const signed = (n) => (n >= 0 ? '+$' : '-$') + fmt(n);

function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}


function useCountUp(target, duration = 1000) {
  const [val, setVal] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    const from = prevRef.current;
    const to   = Number.isFinite(target) ? target : 0;
    prevRef.current = to;
    if (from === to) { setVal(to); return; }
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function MoneyUp({ value }) {
  const v = useCountUp(value ?? 0);
  return <>${fmt(v)}</>;
}

function daysAgo(ts) {
  const d = Math.floor((Date.now() - new Date(ts)) / 86400000);
  if (d <= 0) return 'TODAY';
  if (d === 1) return '1D AGO';
  return `${d}D AGO`;
}

const UP_CELLS = 15, DOWN_CELLS = 7;

export default function OverviewTab5({ rows, settings, livePrices, skinsData = [], inventoryMap }) {
  const [chartYear, setChartYear]         = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const isExcluded = useCallback(r => getItemTypes(r).some(t => {
    const def = settings?.itemTypes?.find(it => it.value === t.value);
    return def?.overrideProfitLoss === true;
  }), [settings?.itemTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableYears = useMemo(() => {
    const years = new Set();
    rows?.forEach(r => { const y = new Date(r.timestamp).getFullYear(); if (y) years.add(y); });
    return [...years].sort((a, b) => b - a);
  }, [rows]);

  // ── Stats ──────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!rows?.length) return {};
    const unsold = getUnsoldRows(rows);
    const totalProfit = getTotalProfit(rows, isExcluded);
    const unsoldCost  = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);

    let unrealizedPnl = 0, marketValue = 0;
    unsold.forEach(r => {
      const lp  = getLivePrice(livePrices, r);
      const buy = parseFloat(r.buyPrice) || 0;
      if (lp) {
        const market = lp.starting_at?.price || 0;
        marketValue   += market;
        unrealizedPnl += market - buy;
      } else marketValue += buy;
    });

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const profit30d = getIncludedSold(rows, isExcluded)
      .filter(r => new Date(r.timestamp) >= cutoff)
      .reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);

    return {
      itemCount: rows.filter(r => r.skin?.value).length,
      soldCount: getSoldRows(rows).length,
      unsoldCount: unsold.length,
      unsoldCost, marketValue, unrealizedPnl, totalProfit, profit30d,
      unrealizedRoi: unsoldCost > 0 ? unrealizedPnl / unsoldCost * 100 : null,
    };
  }, [rows, livePrices, isExcluded]);

  // ── Performance ────────────────────────────────────────────
  const perf = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (!sold.length) return null;
    const profits = sold.map(r => parseFloat(r.profit) || 0);
    const wins = profits.filter(p => p > 0).length;
    let best = sold[0], worst = sold[0];
    sold.forEach(r => {
      const p = parseFloat(r.profit) || 0;
      if (p > (parseFloat(best.profit)  || 0)) best  = r;
      if (p < (parseFloat(worst.profit) || 0)) worst = r;
    });
    return {
      count: sold.length, wins,
      winRate: Math.round(wins / sold.length * 100),
      avg: profits.reduce((s, p) => s + p, 0) / sold.length,
      best, worst,
    };
  }, [rows, isExcluded]);

  // ── Monthly ammo chart ─────────────────────────────────────
  const monthlyData = useMemo(() => {
    const months = MONTH_LABELS.map((label, i) => ({ label, month: i + 1, profit: 0, count: 0 }));
    rows?.filter(r => parseFloat(r.sellPrice) > 0 && !isExcluded(r)).forEach(r => {
      const d = new Date(r.timestamp);
      if (d.getFullYear() === chartYear) {
        const m = months[d.getMonth()];
        m.profit += parseFloat(r.profit) || 0;
        m.count  += 1;
      }
    });
    return months.map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }));
  }, [rows, chartYear, isExcluded]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxAbs = useMemo(() => Math.max(1, ...monthlyData.map(m => Math.abs(m.profit))), [monthlyData]);

  // ── Killfeed (selected month, else latest events) ──────────
  const feed = useMemo(() => {
    if (!rows) return [];
    let src = [...rows].filter(r => r.skin?.value);
    if (selectedMonth) {
      src = src.filter(r => {
        const d = new Date(r.timestamp);
        return parseFloat(r.sellPrice) > 0 && d.getFullYear() === chartYear && d.getMonth() + 1 === selectedMonth;
      }).sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit));
    } else {
      src.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
    return src.slice(0, 9);
  }, [rows, selectedMonth, chartYear]);

  // ── Loadout ────────────────────────────────────────────────
  const loadout = useMemo(() => {
    if (!rows) return [];
    return getUnsoldRows(rows)
      // getUnsoldRows, not a hand-rolled sellPrice test: an item traded away
      // keeps an empty sellPrice forever and would still be listed as a holding.
      .filter(r => parseFloat(r.buyPrice) > 0)
      .sort((a, b) => parseFloat(b.buyPrice) - parseFloat(a.buyPrice))
      .slice(0, 5);
  }, [rows]);

  // ── Arsenal composition (rarity ramp by value rank) ────────
  const composition = useMemo(() => {
    if (!rows) return [];
    const liveRows = rows.filter(r => r.skin?.value && parseFloat(r.buyPrice) > 0);
    const typed = (settings?.itemTypes || [])
      .filter(t => t.value !== 'Normal')
      .map(type => {
        const typeRows = liveRows.filter(r => getItemTypes(r).some(t => t.value === type.value));
        return { name: type.label, value: typeRows.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0) };
      })
      .filter(d => d.value > 0);
    const uncat = liveRows.filter(r => getItemTypes(r).length === 0)
      .reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    if (uncat > 0) typed.push({ name: 'Unassigned', value: uncat });

    typed.sort((a, b) => b.value - a.value);
    const total = typed.reduce((s, d) => s + d.value, 0);
    return typed.map((d, i) => ({
      ...d,
      pct: total > 0 ? d.value / total * 100 : 0,
      ...RARITY[Math.min(i, RARITY.length - 1)],
    }));
  }, [rows, settings]);

  // ── Ticker ─────────────────────────────────────────────────
  const ticker = useMemo(() => {
    if (!rows) return [];
    return getUnsoldRows(rows)
      // getUnsoldRows, not a hand-rolled sellPrice test: an item traded away
      // keeps an empty sellPrice forever and would still be listed as a holding.
      .filter(r => parseFloat(r.buyPrice) > 0)
      .slice(0, 14)
      .map(r => {
        const lp  = getLivePrice(livePrices, r);
        const mkt = lp?.starting_at?.price ?? null;
        const buy = parseFloat(r.buyPrice) || 0;
        const pct = mkt && buy > 0 ? (mkt - buy) / buy * 100 : null;
        return {
          name: r.skin.value,
          cond: COND_ABBR[r.condition?.value] || '',
          price: mkt ?? buy,
          pct,
        };
      });
  }, [rows, livePrices]);

  const noData = !rows?.filter(r => r.skin?.value).length;
  if (noData) {
    return (
      <div className="ov5-root" style={{ textAlign: 'center', padding: '70px 20px' }}>
        <div className="ov5-title"><span className="amber">{'//'}</span> NO SIGNAL</div>
        <div style={{ color: 'var(--ov5-muted)', marginTop: 10, fontSize: 15 }}>Add items in the Tracker tab to initialise the terminal.</div>
      </div>
    );
  }

  const meterBlocks = 12;
  const meterOn = perf ? Math.round(perf.winRate / 100 * meterBlocks) : 0;

  return (
    <div className="ov5-root">

      {/* ── Header ─────────────────────────────────── */}
      <div className="ov5-header">
        <div className="ov5-title">
          <span className="amber">▮▮</span> OVERWATCH <span className="amber">{'//'}</span> PORTFOLIO STATUS
          <span className="ov5-cursor" />
        </div>
        <div className="ov5-header-meta">
          <span>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}</span>
          <span>{stats.itemCount} ASSETS</span>
          <span className="ov5-live"><span className="ov5-live-dot" />{livePrices ? 'FEED LIVE' : 'FEED DOWN'}</span>
        </div>
      </div>

      {/* ── Ticker tape ────────────────────────────── */}
      {ticker.length > 0 && (
        <div className="ov5-ticker">
          <div className="ov5-ticker-track">
            {[0, 1].map(dup => (
              <React.Fragment key={dup}>
                {ticker.map((t, i) => (
                  <div className="ov5-tick" key={`${dup}-${i}`}>
                    <span className="ov5-tick-name">{t.name}{t.cond ? ` · ${t.cond}` : ''}</span>
                    <span className="ov5-tick-price">${fmt(t.price)}</span>
                    {t.pct != null && (
                      <span className={t.pct >= 0 ? 'ov5-tick-up' : 'ov5-tick-down'}>
                        {t.pct >= 0 ? '▲' : '▼'}{Math.abs(t.pct).toFixed(1)}%
                      </span>
                    )}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* ── KPI tiles ──────────────────────────────── */}
      <div className="ov5-kpi-row">
        <div className="ov5-tile">
          <div className="ov5-tile-label">NET WORTH</div>
          <div className="ov5-tile-value"><MoneyUp value={stats.marketValue ?? 0} /></div>
          <div className="ov5-tile-sub">BUFF163 MARK · {stats.unsoldCount} HELD</div>
        </div>
        <div className="ov5-tile">
          <div className="ov5-tile-label">COST BASIS</div>
          <div className="ov5-tile-value"><MoneyUp value={stats.unsoldCost ?? 0} /></div>
          <div className="ov5-tile-sub">CAPITAL DEPLOYED</div>
        </div>
        <div className="ov5-tile">
          <div className="ov5-tile-label">UNREALIZED</div>
          <div className="ov5-tile-value" style={{ color: (stats.unrealizedPnl ?? 0) >= 0 ? 'var(--ov5-green)' : 'var(--ov5-red)' }}>
            {(stats.unrealizedPnl ?? 0) >= 0 ? '+' : '−'}<MoneyUp value={Math.abs(stats.unrealizedPnl ?? 0)} />
          </div>
          <div className="ov5-tile-sub">
            {stats.unrealizedRoi != null ? `${stats.unrealizedRoi >= 0 ? '+' : ''}${stats.unrealizedRoi.toFixed(1)}% ON COST` : '—'}
          </div>
        </div>
        <div className="ov5-tile">
          <div className="ov5-tile-label">REALIZED P/L</div>
          <div className="ov5-tile-value" style={{ color: (stats.totalProfit ?? 0) >= 0 ? 'var(--ov5-green)' : 'var(--ov5-red)' }}>
            {(stats.totalProfit ?? 0) >= 0 ? '+' : '−'}<MoneyUp value={Math.abs(stats.totalProfit ?? 0)} />
          </div>
          <div className="ov5-tile-sub">{signed(stats.profit30d ?? 0)} LAST 30D</div>
        </div>
        {perf && (
          <div className="ov5-tile">
            <div className="ov5-tile-label">WIN RATE</div>
            <div className="ov5-tile-value">{perf.winRate}<span style={{ fontSize: 16, color: 'var(--ov5-muted)' }}>%</span></div>
            <div className="ov5-meter">
              {Array.from({ length: meterBlocks }).map((_, i) => (
                <span key={i} className={i < meterOn ? 'on' : ''} />
              ))}
            </div>
            <div className="ov5-tile-sub" style={{ marginTop: 7 }}>{perf.wins}W / {perf.count - perf.wins}L · {perf.count} FLIPS</div>
          </div>
        )}
      </div>

      {/* ── Main grid ──────────────────────────────── */}
      <div className="ov5-grid">

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>

          {/* Ammo-bar monthly chart */}
          <div className="ov5-panel">
            <div className="ov5-panel-head">
              <div className="ov5-panel-title">P/L BY MONTH</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {availableYears.map(y => (
                  <button key={y} onClick={() => { setChartYear(y); setSelectedMonth(null); }}
                    style={{
                      fontFamily: 'inherit', fontSize: 14.5, fontWeight: 700, letterSpacing: '0.08em',
                      padding: '3px 10px', cursor: 'pointer',
                      background: chartYear === y ? 'rgba(232,163,61,0.14)' : 'transparent',
                      border: `1px solid ${chartYear === y ? 'var(--ov5-amber)' : 'var(--ov5-line)'}`,
                      color: chartYear === y ? 'var(--ov5-amber)' : 'var(--ov5-muted)',
                    }}>
                    {y}
                  </button>
                ))}
              </div>
              <div className="ov5-panel-sub">1 CELL ≈ ${fmt(maxAbs / UP_CELLS)}</div>
            </div>
            <div className="ov5-ammo-chart">
              {monthlyData.map((m) => {
                const cells = Math.min(
                  m.profit >= 0 ? UP_CELLS : DOWN_CELLS,
                  Math.max(m.profit !== 0 ? 1 : 0, Math.round(Math.abs(m.profit) / maxAbs * UP_CELLS)),
                );
                const neg = m.profit < 0;
                const active = selectedMonth === m.month;
                return (
                  <div key={m.month}
                    className={`ov5-ammo-col ${active ? 'active' : ''}`}
                    onClick={() => setSelectedMonth(active ? null : m.month)}
                    title={`${m.label} ${chartYear} · ${signed(m.profit)} · ${m.count} flips`}>
                    <div className="ov5-ammo-up">
                      {!neg && Array.from({ length: cells }).map((_, i) => (
                        <motion.span key={i} className="ov5-ammo-cell"
                          initial={{ opacity: 0, scaleX: 0.4 }}
                          animate={{ opacity: 1 - i * 0.03, scaleX: 1 }}
                          transition={{ delay: 0.15 + m.month * 0.03 + i * 0.035, duration: 0.18 }}
                        />
                      ))}
                    </div>
                    <div className="ov5-ammo-axis" />
                    <div className="ov5-ammo-down">
                      {neg && Array.from({ length: cells }).map((_, i) => (
                        <motion.span key={i} className="ov5-ammo-cell neg"
                          initial={{ opacity: 0, scaleX: 0.4 }}
                          animate={{ opacity: 1 - i * 0.03, scaleX: 1 }}
                          transition={{ delay: 0.15 + m.month * 0.03 + i * 0.035, duration: 0.18 }}
                        />
                      ))}
                    </div>
                    <div className="ov5-ammo-label">{m.label}</div>
                    <div className="ov5-ammo-value" style={{ color: m.profit === 0 ? 'var(--ov5-dim)' : m.profit > 0 ? 'var(--ov5-green)' : 'var(--ov5-red)' }}>
                      {m.profit !== 0 ? signed(m.profit) : '·'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Killfeed */}
          <div className="ov5-panel">
            <div className="ov5-panel-head">
              <div className="ov5-panel-title">KILLFEED</div>
              <div className="ov5-panel-sub">
                {selectedMonth
                  ? `${MONTH_LABELS[selectedMonth - 1]} ${chartYear} FLIPS — CLICK MONTH TO CLEAR`
                  : 'LATEST EVENTS'}
              </div>
            </div>
            <div className="ov5-feed">
              {feed.length === 0 && (
                <div style={{ padding: '18px 6px', color: 'var(--ov5-dim)', fontSize: 14.5 }}>NO EVENTS IN WINDOW</div>
              )}
              {feed.map((r, i) => {
                const sold   = parseFloat(r.sellPrice) > 0;
                const profit = parseFloat(r.profit) || 0;
                const cls    = sold ? (profit >= 0 ? 'win' : 'loss') : 'hold';
                const img    = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                return (
                  <motion.div key={`${selectedMonth}-${i}`} className={`ov5-feed-row ${cls}`}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.25 }}>
                    <span className="ov5-feed-time">{daysAgo(r.timestamp)}</span>
                    {img && <img src={img} alt="" className="ov5-feed-img" />}
                    <span className="ov5-feed-name">
                      {r.skin?.value}
                      {r.condition?.value && <span className="cond">{COND_ABBR[r.condition.value] || r.condition.value}</span>}
                    </span>
                    <span className={`ov5-feed-tag ${cls}`}>
                      {sold ? signed(profit) : `HOLD $${fmt(parseFloat(r.buyPrice) || 0)}`}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>

          {/* Loadout */}
          <div className="ov5-panel">
            <div className="ov5-panel-head">
              <div className="ov5-panel-title">LOADOUT</div>
              <div className="ov5-panel-sub">TOP 5 BY COST</div>
            </div>
            <div className="ov5-loadout">
              {loadout.length === 0 && (
                <div style={{ padding: '18px 6px', color: 'var(--ov5-dim)', fontSize: 14.5 }}>ARSENAL EMPTY</div>
              )}
              {loadout.map((r, i) => {
                const img  = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                const lp   = getLivePrice(livePrices, r);
                const mkt  = lp?.starting_at?.price;
                const buy  = parseFloat(r.buyPrice) || 0;
                const diff = mkt ? mkt - buy : null;
                const inv    = r.assetId ? inventoryMap?.get(r.assetId) : null;
                const locked = getTradeLock(r, inv).locked;
                return (
                  <motion.div key={i} className="ov5-slot"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + i * 0.07, duration: 0.3 }}>
                    <span className="ov5-slot-rank">{`SLOT-${i + 1}`}</span>
                    {img && <img src={img} alt="" className="ov5-slot-img" />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="ov5-slot-name">{r.skin?.value}</div>
                      <div className="ov5-slot-meta">
                        {COND_ABBR[r.condition?.value] || r.condition?.value || '—'}
                        {locked && <span style={{ color: 'var(--ov5-amber)' }}> · LOCKED</span>}
                      </div>
                    </div>
                    <div className="ov5-slot-price">
                      <div className="buy">${fmt(buy)}</div>
                      {diff !== null && (
                        <div className="delta" style={{ color: diff >= 0 ? 'var(--ov5-green)' : 'var(--ov5-red)' }}>
                          {diff >= 0 ? '▲' : '▼'}${fmt(Math.abs(diff))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Arsenal composition */}
          <div className="ov5-panel">
            <div className="ov5-panel-head">
              <div className="ov5-panel-title">ARSENAL COMP</div>
              <div className="ov5-panel-sub">COST BASIS BY CATEGORY</div>
            </div>
            <div className="ov5-comp">
              {composition.length === 0 ? (
                <div style={{ color: 'var(--ov5-dim)', fontSize: 14.5 }}>NO CATEGORISED ASSETS</div>
              ) : (
                <>
                  <div className="ov5-comp-bar">
                    {composition.map((c, i) => (
                      <motion.div key={i} className="ov5-comp-seg"
                        style={{ background: c.color }}
                        title={`${c.name} · $${fmt(c.value)} · ${c.pct.toFixed(1)}%`}
                        initial={{ flexGrow: 0.0001 }}
                        animate={{ flexGrow: c.pct }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.05 }}
                      />
                    ))}
                  </div>
                  <div className="ov5-comp-legend">
                    {composition.map((c, i) => (
                      <div key={i} className="ov5-comp-row">
                        <span className="ov5-comp-swatch" style={{ background: c.color }} />
                        <span className="ov5-comp-name">{c.name}</span>
                        <span style={{ fontSize: 13.5, color: c.color, letterSpacing: '0.1em' }}>{c.tier}</span>
                        <span className="ov5-comp-dots" />
                        <span className="ov5-comp-pct">{c.pct.toFixed(0)}%</span>
                        <span className="ov5-comp-val">${fmt(c.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Intel */}
          {perf && (
            <div className="ov5-panel">
              <div className="ov5-panel-head">
                <div className="ov5-panel-title">INTEL</div>
                <div className="ov5-panel-sub">CAREER RECORD</div>
              </div>
              <div className="ov5-intel">
                <div className="ov5-intel-row">
                  <span className="ov5-intel-key">BEST FLIP</span>
                  <span className="ov5-intel-dots" />
                  <span style={{ fontSize: 14.5, color: 'var(--ov5-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{perf.best.skin?.value}</span>
                  <span className="ov5-intel-val" style={{ color: 'var(--ov5-green)' }}>{signed(parseFloat(perf.best.profit) || 0)}</span>
                </div>
                <div className="ov5-intel-row">
                  <span className="ov5-intel-key">WORST FLIP</span>
                  <span className="ov5-intel-dots" />
                  <span style={{ fontSize: 14.5, color: 'var(--ov5-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{perf.worst.skin?.value}</span>
                  <span className="ov5-intel-val" style={{ color: (parseFloat(perf.worst.profit) || 0) >= 0 ? 'var(--ov5-green)' : 'var(--ov5-red)' }}>
                    {signed(parseFloat(perf.worst.profit) || 0)}
                  </span>
                </div>
                <div className="ov5-intel-row">
                  <span className="ov5-intel-key">AVG / FLIP</span>
                  <span className="ov5-intel-dots" />
                  <span className="ov5-intel-val" style={{ color: perf.avg >= 0 ? 'var(--ov5-green)' : 'var(--ov5-red)' }}>{signed(perf.avg)}</span>
                </div>
                <div className="ov5-intel-row">
                  <span className="ov5-intel-key">30D NET</span>
                  <span className="ov5-intel-dots" />
                  <span className="ov5-intel-val" style={{ color: (stats.profit30d ?? 0) >= 0 ? 'var(--ov5-green)' : 'var(--ov5-red)' }}>{signed(stats.profit30d ?? 0)}</span>
                </div>
                <div className="ov5-intel-row">
                  <span className="ov5-intel-key">FLIPS CLOSED</span>
                  <span className="ov5-intel-dots" />
                  <span className="ov5-intel-val">{perf.count}</span>
                </div>
                <div className="ov5-intel-row">
                  <span className="ov5-intel-key">ACTIVE HOLDINGS</span>
                  <span className="ov5-intel-dots" />
                  <span className="ov5-intel-val">{stats.unsoldCount}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────── */}
      <div className="ov5-footer">
        <span>OVERWATCH TERMINAL v5</span>
        <span>MARK: BUFF163</span>
        <span style={{ marginLeft: 'auto' }}>{stats.soldCount} CLOSED · {stats.unsoldCount} OPEN · EOF</span>
      </div>
    </div>
  );
}
